import { PLAN_DISPLAY, PLAN_KEYS } from "@giftforward/entitlements";
import { AddOutlined } from "@mui/icons-material";
import { Box, Chip, IconButton } from "@mui/material";
import { useState } from "react";
import { fetchOrganizations } from "@/api/organizations";
import Blotter, { type BlotterConfig, type BlotterData } from "@/components/Blotter";
import type { AppliedFilter, FilterField } from "@/components/BlotterFilterBar";
import CreateOrgDialog from "@/components/usersOrgs/CreateOrgDialog";
import OrgDetail from "@/components/usersOrgs/OrgDetail";
import { useOrganizationFilterOptions, useOrganizations } from "@/hooks/useOrganizations";
import { usePermissions } from "@/hooks/usePermissions";
import { useAccountLeads } from "@/hooks/useUsers";
import type { Organization } from "@/interfaces/organization";
import { subscriptionDisplayStatus, subscriptionStatusColor } from "@/lib/subscriptionStatus";
import { accountLeadOptions, orgListParams } from "@/utils/usersOrgs-helper";

// Plan keys, matching subscription_plans.plan_key, labelled as the plan badge reads.
const PLAN_OPTIONS: FilterField["options"] = PLAN_KEYS.map((key) => ({
  value: key,
  label: PLAN_DISPLAY[key].badgeLabel,
}));

// Lifecycle is a smallint set by a churn-eval job that doesn't exist yet — all 0.
const LIFECYCLE = ["New", "Active", "At Risk", "Churned", "Reactivated"];
function lifecycleLabel(n: number): string {
  return LIFECYCLE[n] ?? "Active";
}

function titleCase(s: string | null): string {
  if (!s) return "—";
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** Data hook for the orgs blotter — maps blotter query state to the organizations API. */
function useOrgsData(q: {
  search: string;
  filters: AppliedFilter[];
  sortBy: string;
  sortDir: "asc" | "desc";
  page: number;
  limit: number;
}): BlotterData<Organization> {
  const { data, isLoading } = useOrganizations({
    ...orgListParams({ search: q.search, filters: q.filters }),
    sortBy: q.sortBy,
    sortDir: q.sortDir,
    page: q.page,
    limit: q.limit,
  });
  return {
    data: data?.data ?? [],
    total: data?.total ?? 0,
    totalPages: data?.totalPages ?? 1,
    isLoading,
  };
}

export default function OrgsBlotter() {
  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const { can } = usePermissions();
  // Same gate as OrgDetail's Account lead field: who inside GFG owns an account is an internal
  // assignment, so the column is absent for everyone else rather than shown read-only.
  const canSeeAccountLead = can("edit", "USER", "all");
  const { data: orgFilterOptions } = useOrganizationFilterOptions();
  const { data: leads } = useAccountLeads(canSeeAccountLead);

  if (selectedOrg) {
    return <OrgDetail org={selectedOrg} onBack={() => setSelectedOrg(null)} />;
  }

  const config: BlotterConfig<Organization> = {
    title: "Organizations",
    searchPlaceholder: "Search and filter organizations",
    itemNoun: "org",
    minWidth: 820,
    rowKey: (o) => o.id,
    // Creating an org requires create on ORGANIZATION (GFG Super Admin), matching the backend gate.
    headerAction: can("create", "ORGANIZATION") ? (
      <IconButton
        onClick={() => setCreateOpen(true)}
        title="New organization"
        aria-label="New organization"
        sx={{
          bgcolor: "var(--color-rodeo-black)",
          color: "var(--color-white)",
          borderRadius: "var(--radius-btn)",
          width: 48,
          height: 48,
          "&:hover": { bgcolor: "var(--color-rodeo-black)", opacity: 0.9 },
        }}
      >
        <AddOutlined />
      </IconButton>
    ) : undefined,
    exportFilename: "organizations.csv",
    exportFetch: async (q) => {
      const res = await fetchOrganizations({
        ...orgListParams({ search: q.search, filters: q.filters }),
        page: q.page,
        limit: q.limit,
      });
      return { data: res.data, totalPages: res.totalPages };
    },
    useData: useOrgsData,
    filterFields: [
      { id: "organization", label: "Organization", options: orgFilterOptions ?? [] },
      { id: "plan", label: "Plan", options: PLAN_OPTIONS },
      {
        id: "lifecycle",
        label: "Lifecycle",
        options: LIFECYCLE.map((l) => ({ value: l, label: l })),
      },
      ...(canSeeAccountLead
        ? [
            {
              id: "accountLead",
              label: "Account Lead",
              options: accountLeadOptions(leads ?? []),
            } satisfies FilterField,
          ]
        : []),
    ],
    filterPanel: true,
    columns: [
      {
        key: "name",
        label: "Organization",
        sortable: true,
        sortValue: (o) => o.name,
        render: (o) => (
          <button
            type="button"
            onClick={() => setSelectedOrg(o)}
            style={{
              border: "none",
              background: "none",
              padding: 0,
              cursor: "pointer",
              fontWeight: 600,
              fontFamily: "var(--font-lato)",
              fontSize: 14,
              color: "var(--color-rodeo-black)",
              textAlign: "left",
            }}
          >
            {o.name}
          </button>
        ),
      },
      {
        key: "plan",
        label: "Plan",
        sortable: true,
        sortValue: (o) => o.plan ?? "",
        render: (o) => titleCase(o.plan),
      },
      {
        // The plan column says WHICH plan; this says whether it is being paid for. An organization
        // on Pro and past due looks identical to a healthy one without it, and past due is the
        // state that needs chasing.
        key: "subscription",
        label: "Status",
        sortValue: (o) => subscriptionDisplayStatus(o),
        render: (o) => {
          const status = subscriptionDisplayStatus(o);
          if (status === "—") return <Box sx={{ color: "var(--color-graphite)" }}>—</Box>;
          return (
            <Chip
              size="small"
              variant="outlined"
              label={status}
              color={subscriptionStatusColor(status)}
            />
          );
        },
        exportValue: (o) => subscriptionDisplayStatus(o),
      },
      {
        key: "userCount",
        label: "Users",
        sortable: true,
        sortValue: (o) => o.userCount,
        render: (o) => o.userCount,
      },
      {
        key: "balance",
        label: "Credit Balance",
        sortable: false,
        exportValue: (o) => `${Number(o.creditBalance).toLocaleString()} credits`,
        render: (o) => `${Number(o.creditBalance).toLocaleString()} credits`,
      },
      {
        key: "lifecycle",
        label: "Lifecycle",
        sortable: true,
        sortValue: (o) => o.lifecycle,
        exportValue: (o) => lifecycleLabel(o.lifecycle),
        render: (o) => lifecycleLabel(o.lifecycle),
      },
      ...(canSeeAccountLead
        ? [
            {
              key: "accountLead",
              label: "Account Lead",
              sortable: true,
              sortValue: (o: Organization) => o.accountLead ?? "",
              render: (o: Organization) => o.accountLead ?? "—",
            },
          ]
        : []),
      {
        key: "createdAt",
        label: "Date Added",
        sortable: true,
        sortValue: (o) => o.createdAt,
        exportValue: (o) => o.createdAt.slice(0, 10),
        render: (o) => (
          <span style={{ color: "var(--color-graphite)" }}>{o.createdAt.slice(0, 10)}</span>
        ),
      },
    ],
  };

  return (
    <>
      <Blotter config={config} />
      <CreateOrgDialog open={createOpen} onClose={() => setCreateOpen(false)} />
    </>
  );
}
